import { PsaOptionModel, PsaTicketFieldSupport } from './onboarding.model';

// PSA configuration definitions based on current connector implementations
export const PSA_OPTIONS: PsaOptionModel[] = [
  {
    id: 'cwmanage',
    name: 'ConnectWise Manage',
    code: 'CWM',
    description: 'ConnectWise PSA integration for ticket management',
    imagePath: '/assets/img/connectors/cwmanage_circle.png',
    isActive: true,
    helpText: 'Configure your ConnectWise Manage integration settings to enable ticket management',
    helpUrl: 'https://radials.io/chatconnectwise',
    boardTerminology: 'board',
    configFields: [
      {
        name: 'companyName',
        label: 'ConnectWise Company ID',
        type: 'text',
        placeholder: 'Your ConnectWise company name',
        required: true,
        helpText: 'Your ConnectWise company identifier (this is required for API authentication)'
      },
      {
        name: 'siteUrl',
        label: 'ConnectWise URL',
        type: 'url',
        placeholder: 'https://your-company.connectwiseapps.com',
        required: true,
        helpText: 'The web address where you log in to ConnectWise Manage (example: https://yourcompany.connectwiseapps.com)'
      },
      {
        name: 'hostName',
        label: 'Ticket & Contact Links',
        type: 'text',
        placeholder: 'api-na.myconnectwise.net',
        required: true,
        helpText: 'The web address to use when opening tickets and contacts. This is usually the same as your ConnectWise URL above.'
      },
      {
        name: 'publicKey',
        label: 'API Public Key',
        type: 'text',
        placeholder: 'Your public API key',
        required: true,
        helpText: 'Your ConnectWise API public key (create this in ConnectWise under System > Members > API Keys)'
      },
      {
        name: 'privateKey',
        label: 'API Private Key',
        type: 'password',
        placeholder: 'Your private API key',
        required: true,
        helpText: 'Your ConnectWise API private key (you\'ll only see this once when creating the API keys)'
      }
    ]
  },
  {
    id: 'autotask',
    name: 'Autotask PSA',
    code: 'AT',
    description: 'Autotask PSA integration for ticket management',
    imagePath: '/assets/img/connectors/autotask_circle.png',
    isActive: true,
    helpText: 'Configure your Autotask PSA integration settings to enable ticket management',
    helpUrl: 'https://radials.io/chatautotask',
    boardTerminology: 'board',
    configFields: [
      {
        name: 'userName',
        label: 'API Username',
        type: 'text',
        placeholder: 'integration@yourcompany.com',
        required: true,
        helpText: 'Your Autotask API username (an email address format like integration@yourcompany.com)'
      },
      {
        name: 'secret',
        label: 'API Secret',
        type: 'password',
        placeholder: 'Your API secret',
        required: true,
        helpText: 'Your Autotask API secret key (generate this in Autotask under Admin > Resources > API User)'
      }
    ]
  },
  {
    id: 'bms',
    name: 'Kaseya BMS',
    code: 'BMS',
    description: 'Kaseya BMS (formerly Vorex) integration',
    imagePath: '/assets/img/connectors/bms_circle.png',
    isActive: true,
    helpText: 'Configure your Kaseya BMS integration settings to enable ticket management',
    helpUrl: 'https://radials.io/chatbms',
    boardTerminology: 'queue',
    configFields: [
      {
        name: 'companyName',
        label: 'Company Name',
        type: 'text',
        placeholder: 'Your company or tenant name',
        required: true,
        helpText: 'Your Kaseya BMS company or tenant identifier'
      },
      {
        name: 'siteUrl',
        label: 'Kaseya BMS URL',
        type: 'url',
        placeholder: 'https://api.bms.kaseya.com',
        required: true,
        helpText: 'The Kaseya BMS API address for your region (example: https://api.bms.kaseya.com). This is not the address you use to log in to BMS.'
      },
      {
        name: 'hostName',
        label: 'Ticket & Contact Links',
        type: 'url',
        placeholder: 'https://na1bms.kaseya.com',
        required: false,
        helpText: 'The web address where you log in to Kaseya BMS, used to build "View Ticket" and "View Contact" links. Leave blank to hide those links.'
      },
      {
        name: 'userName',
        label: 'API Username',
        type: 'text',
        placeholder: 'Your username',
        required: true,
        helpText: 'Username for API access to Kaseya BMS'
      },
      {
        name: 'secret',
        label: 'API Password',
        type: 'password',
        placeholder: 'Your API secret',
        required: true,
        helpText: 'Password for your Kaseya BMS API user account'
      }
    ]
  },
  {
    id: 'freshdesk',
    name: 'Freshdesk',
    code: 'FD',
    description: 'Freshdesk ticketing system integration',
    imagePath: '/assets/img/connectors/freshdesk_circle.png',
    isActive: false,
    helpText: 'Configure your Freshdesk integration settings to enable ticket management',
    helpUrl: 'PSA|Freshdesk',
    boardTerminology: 'board',
    configFields: [
      {
        name: 'siteUrl',
        label: 'Site URL',
        type: 'url',
        placeholder: 'https://your-domain.freshdesk.com',
        required: true,
        helpText: 'Your Freshdesk domain URL'
      },
      {
        name: 'privateKey',
        label: 'API Key',
        type: 'password',
        placeholder: 'Your Freshdesk API key',
        required: true,
        helpText: 'Your Freshdesk API key (found in Profile Settings)'
      }
    ]
  },
  {
    id: 'halo',
    name: 'HaloPSA',
    code: 'HALO',
    description: 'HaloPSA integration for ticket management',
    imagePath: '/assets/img/connectors/halo_circle.png',
    isActive: true,
    helpText: 'Configure your HaloPSA integration settings to enable ticket management',
    helpUrl: 'https://radials.io/chathalo',
    boardTerminology: 'team',
    configFields: [
      {
        name: 'siteUrl',
        label: 'HaloPSA URL',
        type: 'url',
        placeholder: 'https://yourcompany.halopsa.com',
        required: true,
        helpText: 'The web address where you access HaloPSA (example: https://yourcompany.halopsa.com)'
      },
      {
        name: 'publicKey',
        label: 'Application Client ID',
        type: 'text',
        placeholder: 'Your application client ID',
        required: true,
        helpText: 'The Client ID from your HaloPSA API application (create this under Configuration > Integrations > HaloPSA API)'
      },
      {
        name: 'privateKey',
        label: 'Application Client Secret',
        type: 'password',
        placeholder: 'Your application client secret',
        required: true,
        helpText: 'The Client Secret from your HaloPSA API application (you\'ll only see this once when creating the application)'
      }
    ]
  },
  {
    id: 'syncromsp',
    name: 'Syncro MSP',
    code: 'SYNC',
    description: 'Syncro MSP integration for ticket management',
    imagePath: '/assets/img/connectors/syncromsp_circle.png',
    isActive: true,
    helpText: 'Configure your Syncro MSP integration settings to enable ticket management',
    helpUrl: 'https://radials.io/chatsyncro',
    boardTerminology: 'board',
    // Syncro has no board/queue/ticket-list grouping and does not track ticket sources, so those
    // two Step 3 dropdowns are hidden. Type, status and priority ARE supported: type + priority come
    // from GET /settings (ticket.problem_types / ticket.priorities) and status from
    // /tickets/settings (ticket_status_list). See CHAT-426.
    supportedTicketFields: { board: false, source: false },
    configFields: [
      {
        name: 'siteUrl',
        label: 'Your Syncro Subdomain',
        type: 'text',
        placeholder: 'acme',
        required: true,
        helpText: 'Just the subdomain part (example: if you use "acme.syncromsp.com", enter only "acme")'
      },
      {
        name: 'secret',
        label: 'API Key',
        type: 'password',
        placeholder: 'Your Syncro API key',
        required: true,
        helpText: 'Your Syncro API key (find this in Syncro under Admin > API Tokens)'
      }
    ]
  },
  {
    // CHAT-1214: CloudRadial's own help desk, added by a SuperAdmin from Add Connector on an onboarded
    // tenant. Defined here because the Add Connector dialog and the connector edit page read their
    // form from PSA_OPTIONS, but isActive is false so onboarding (which lists the active entries)
    // never offers it.
    id: 'zendesk',
    name: 'Zendesk',
    code: 'ZD',
    description: 'Creates tickets in the CloudRadial help desk',
    imagePath: '/assets/img/connectors/zendesk_circle.png',
    isActive: false,
    helpText: 'Connect a Zendesk help desk with an OAuth client',
    boardTerminology: 'board',
    // Zendesk tickets take a group and a status; source, type and priority are not defaults here.
    supportedTicketFields: { source: false, type: false, priority: false },
    configFields: [
      {
        name: 'siteUrl',
        label: 'Zendesk Subdomain',
        type: 'text',
        placeholder: 'cloudradial',
        required: true,
        helpText: 'The part before .zendesk.com in your Zendesk address (example: cloudradial for cloudradial.zendesk.com)'
      },
      {
        name: 'publicKey',
        label: 'OAuth Client ID',
        type: 'text',
        placeholder: 'Your OAuth client ID',
        required: true,
        helpText: 'Create a Confidential OAuth client in Zendesk Admin Center, with the read and write scopes, or no scope restriction.'
      },
      {
        name: 'privateKey',
        label: 'OAuth Client Secret',
        type: 'password',
        placeholder: 'Your OAuth client secret',
        required: true,
        helpText: 'The client secret Zendesk shows when you create the OAuth client (it is shown only once)'
      }
    ]
  },
  {
    id: 'jira',
    name: 'Jira Service Management',
    code: 'JIRA',
    description: 'Atlassian Jira integration for ticket management',
    imagePath: '/assets/img/connectors/jira_circle.png',
    isActive: false,
    helpText: 'Configure your Jira Service Management integration settings to enable ticket management',
    helpUrl: 'PSA|Jira',
    boardTerminology: 'board',
    configFields: [
      {
        name: 'hostName',
        label: 'Instance',
        type: 'text',
        placeholder: 'your-domain.atlassian.net',
        required: true,
        helpText: 'Your Jira instance URL'
      },
      {
        name: 'userName',
        label: 'User Name',
        type: 'text',
        placeholder: 'Your email address',
        required: true,
        helpText: 'The email address for API access'
      },
      {
        name: 'secret',
        label: 'API Key',
        type: 'password',
        placeholder: 'Your Jira API token',
        required: true,
        helpText: 'Your Jira API token (not your password)'
      }
    ]
  }
];

/**
 * Get the board terminology for a PSA based on its ID or connector ID.
 * Different PSAs use different terms for 'board' (e.g., 'queue' for BMS, 'team' for Halo).
 *
 * @param type - The original type (e.g., 'board')
 * @param psaId - The PSA ID (e.g., 'bms', 'halo')
 * @param connectorId - Optional connector ID to extract PSA type from (e.g., ':bms:', ':halo:')
 * @returns The mapped type for the PSA
 */
export function getBoardTerminology(type: string, psaId?: string, connectorId?: string): string {
  if (type !== 'board') {
    return type;
  }

  return findPsaOption(psaId, connectorId)?.boardTerminology ?? type;
}

/**
 * Resolve a PSA option from its id (preferred) or, failing that, from a connector id that
 * embeds the PSA id (e.g. ':syncromsp:'). Returns undefined when no PSA matches.
 */
function findPsaOption(psaId?: string, connectorId?: string): PsaOptionModel | undefined {
  const psaIdLower = psaId?.toLowerCase();
  if (psaIdLower) {
    const psa = PSA_OPTIONS.find(p => p.id === psaIdLower);
    if (psa) {
      return psa;
    }
  }

  // Fall back to extracting PSA from connector ID pattern (e.g., ':bms:', ':halo:')
  const connectorLower = connectorId?.toLowerCase() || '';
  for (const psa of PSA_OPTIONS) {
    if (connectorLower.includes(`:${psa.id}:`)) {
      return psa;
    }
  }

  return undefined;
}

const DEFAULT_TICKET_FIELD_SUPPORT: PsaTicketFieldSupport = {
  board: true,
  source: true,
  type: true,
  status: true,
  priority: true,
};

/**
 * Resolve which Step 3 ticket-default dropdowns a PSA supports. Unknown PSAs (and any field
 * a PSA doesn't explicitly override) default to supported, so only PSAs that declare a field
 * unsupported (e.g. Syncro: board, source) have it hidden.
 */
export function getPsaSupportedTicketFields(psaId?: string, connectorId?: string): PsaTicketFieldSupport {
  const overrides = findPsaOption(psaId, connectorId)?.supportedTicketFields ?? {};
  return { ...DEFAULT_TICKET_FIELD_SUPPORT, ...overrides };
}